/**
 * Training Viewer Page
 *
 * Mobile-first content viewer for training materials.
 * Supports video playback with progress tracking and
 * PDF document viewing with scroll-based progress.
 * Edge-to-edge video/PDF via -mx-4 breakout.
 * Container is provided by OperatorLayout — no wrapper needed.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Video,
  FileText,
  Clock,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Download,
  Maximize,
} from 'lucide-react';
import {
  useTrainingDetail,
  useUpdateProgress,
  useCompleteTraining,
} from '../../hooks/useOperatorPortal';
import { OperatorLoadingSkeleton } from './components/OperatorLoadingSkeleton';
import { OperatorEmptyState } from './components/OperatorEmptyState';
import { OperatorCard } from './components/OperatorCard';
import { BottomSheet } from './components/BottomSheet';
import { ContextualTooltip } from './components/ContextualTooltip';
import { WhyBadge } from './components/WhyBadge';
import { FeedbackWidget } from './components/FeedbackWidget';
import { formatDuration } from './utils';

export function TrainingViewerPage() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();

  const { data: training, isLoading, error } = useTrainingDetail(assignmentId ?? null);
  const updateProgress = useUpdateProgress();
  const completeTraining = useCompleteTraining();

  // Phase 25.4: Quiz materials have a sentinel s3Key (`quiz:${sectionId}`)
  // and no presigned URL — rendering the TrainingViewer for them would show
  // a broken video player. Redirect to the Phase 21.6 quiz player instead.
  // The TrainingCard component routes quiz clicks directly, so this branch
  // only fires if someone lands on `/operator/content/:assignmentId` for a
  // quiz material via a bookmark, notification link, or legacy route.
  useEffect(() => {
    if (training && training.material.materialType === 'quiz') {
      const quizSectionId = training.material.sectionId;
      if (quizSectionId) {
        navigate(`/operator/practice/quiz/${quizSectionId}`, { replace: true });
      } else {
        // No section reference — bounce back to the content list with a
        // graceful fallback rather than leaving the user on a broken viewer.
        navigate('/operator/content', { replace: true });
      }
    }
  }, [training, navigate]);

  const [localProgress, setLocalProgress] = useState(0);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const lastSavedProgress = useRef(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Sync local progress with server progress
  useEffect(() => {
    if (training) {
      setLocalProgress(training.progress);
      lastSavedProgress.current = training.progress;
    }
  }, [training]);

  // Save progress when it increases by 10%
  const saveProgress = useCallback(
    (progress: number) => {
      if (!assignmentId || progress <= lastSavedProgress.current) return;

      // Only save at 10% intervals
      const roundedProgress = Math.floor(progress / 10) * 10;
      if (roundedProgress > lastSavedProgress.current) {
        lastSavedProgress.current = roundedProgress;
        updateProgress.mutate({
          assignmentId,
          progress: roundedProgress,
        });
      }
    },
    [assignmentId, updateProgress]
  );

  // Video progress handler
  const handleVideoProgress = useCallback(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const progress = Math.floor((video.currentTime / video.duration) * 100);
    setLocalProgress(Math.max(localProgress, progress));
    saveProgress(progress);
  }, [localProgress, saveProgress]);

  // Video ended handler
  const handleVideoEnded = useCallback(() => {
    setLocalProgress(100);
    saveProgress(100);
    setShowCompleteModal(true);
  }, [saveProgress]);

  // Mark as complete
  const handleMarkComplete = async () => {
    if (!assignmentId) return;

    try {
      await completeTraining.mutateAsync(assignmentId);
      setShowCompleteModal(false);
      navigate('/operator/content?justCompleted=true');
    } catch (err) {
      console.error('Failed to mark complete:', err);
    }
  };

  if (isLoading) {
    return <OperatorLoadingSkeleton variant="page" />;
  }

  if (error || !training) {
    return (
      <OperatorEmptyState
        icon={AlertTriangle}
        title="Training Not Found"
        description="This training assignment could not be found or you don't have access to it."
        ctaLabel="Back to Training"
        ctaHref="/operator/content"
      />
    );
  }

  // Phase 25.4: If the material is a quiz, the effect above is navigating
  // away — render the loading skeleton to avoid a flash of broken UI.
  if (training.material.materialType === 'quiz') {
    return <OperatorLoadingSkeleton variant="page" />;
  }

  const isVideo = training.material.materialType === 'video';
  const isCompleted = training.status === 'completed';

  return (
    <div className="space-y-4">
      {/* Back button + Title */}
      <div style={{ animation: 'fadeInUp 0.4s ease-out' }}>
        <Link
          to="/operator/content"
          className="flex min-h-[48px] items-center gap-2 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2 focus-visible:rounded-lg"
        >
          <ArrowLeft size={18} strokeWidth={1.5} className="text-op-text-secondary" />
          <span className="text-sm font-medium text-op-text-secondary">Back to Training</span>
        </Link>

        <h1 className="mt-2 text-lg font-semibold text-op-text-primary line-clamp-2">
          {training.material.title}
        </h1>

        {/* Meta row */}
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
              isVideo
                ? 'bg-violet-100 text-violet-700'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            {isVideo ? <Video size={11} strokeWidth={1.5} /> : <FileText size={11} strokeWidth={1.5} />}
            {isVideo ? 'Video' : 'Document'}
          </span>
          {training.material.duration && isVideo && (
            <span className="flex items-center gap-1 text-op-text-tertiary">
              <Clock size={13} strokeWidth={1.5} />
              {formatDuration(training.material.duration)}
            </span>
          )}
          {isCompleted ? (
            <span className="flex items-center gap-1 font-medium text-emerald-600">
              <CheckCircle2 size={13} strokeWidth={1.5} />
              Completed
            </span>
          ) : (
            <span className="font-medium text-guild-green">{localProgress}% complete</span>
          )}
          {training.sopId && (
            <Link
              to={`/operator/sops/${training.sopId}`}
              className="flex items-center gap-1 font-medium text-guild-green"
            >
              <FileText size={11} strokeWidth={1.5} />
              View SOP
            </Link>
          )}
        </div>
      </div>

      {/* Content Viewer — edge-to-edge */}
      {isVideo ? (
        <div className="-mx-4" style={{ animation: 'fadeInUp 0.4s ease-out 0.06s both' }}>
          <div className="relative aspect-video bg-black">
            <video
              ref={videoRef}
              src={training.material.streamUrl}
              controls
              className="h-full w-full"
              onTimeUpdate={handleVideoProgress}
              onEnded={handleVideoEnded}
              preload="metadata"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      ) : (
        <div className="-mx-4" style={{ animation: 'fadeInUp 0.4s ease-out 0.06s both' }}>
          {/* PDF Viewer */}
          <div className="relative h-[60vh]">
            <embed
              src={training.material.streamUrl}
              type="application/pdf"
              className="h-full w-full"
            />

            {/* Fallback for browsers that don't support embed */}
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50/90 opacity-0 transition-opacity">
              <p className="mb-4 text-sm text-op-text-secondary">Unable to display PDF</p>
              <a
                href={training.material.streamUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-xl bg-guild-green px-5 py-3 text-sm font-semibold text-white active:bg-guild-green/90 transition-colors duration-150"
              >
                <Maximize size={16} strokeWidth={1.5} />
                Open PDF in New Tab
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {!isCompleted && (
        <div ref={progressBarRef} style={{ animation: 'fadeInUp 0.4s ease-out 0.12s both' }}>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-guild-green transition-all duration-500"
              style={{ width: `${localProgress}%` }}
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            <span className="text-xs text-op-text-tertiary">Progress</span>
            <span className="text-xs text-op-text-tertiary">{localProgress}%</span>
          </div>

          <ContextualTooltip
            tooltipId="training-viewer-progress"
            targetRef={progressBarRef}
            message="Your progress is tracked automatically as you watch."
            position="above"
          />
        </div>
      )}

      {/* Controls */}
      {isVideo ? (
        <OperatorCard className="space-y-3">
          {/* Download */}
          <a
            href={training.material.streamUrl}
            download
            className="flex w-full items-center justify-center gap-2 min-h-[48px] rounded-xl bg-op-muted px-6 py-3 text-sm font-semibold text-op-text-primary active:bg-gray-200 transition-all duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          >
            <Download size={18} strokeWidth={1.5} />
            Download
          </a>

          {/* Mark Complete (conditional) */}
          {!isCompleted && localProgress >= 80 && (
            <button
              type="button"
              onClick={() => setShowCompleteModal(true)}
              className="flex w-full items-center justify-center gap-2 min-h-[48px] rounded-xl bg-guild-green px-6 py-3 text-sm font-semibold text-white active:scale-[0.98] active:bg-guild-green/90 transition-all duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
            >
              <CheckCircle2 size={18} strokeWidth={1.5} />
              Mark Complete
            </button>
          )}
        </OperatorCard>
      ) : (
        /* PDF controls */
        !isCompleted && (
          <OperatorCard className="space-y-3">
            <p className="text-sm text-op-text-secondary">
              Finished reading? Mark your progress or complete the document.
            </p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-op-text-tertiary">Progress:</span>
                <select
                  value={localProgress}
                  onChange={(e) => {
                    const newProgress = parseInt(e.target.value, 10);
                    setLocalProgress(newProgress);
                    saveProgress(newProgress);
                  }}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
                >
                  {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((p) => (
                    <option key={p} value={p} disabled={p < lastSavedProgress.current}>
                      {p}%
                    </option>
                  ))}
                </select>
              </div>
              {localProgress >= 80 && (
                <button
                  type="button"
                  onClick={() => setShowCompleteModal(true)}
                  className="flex items-center gap-2 rounded-xl bg-guild-green px-4 py-2 text-sm font-semibold text-white active:bg-guild-green/90 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
                >
                  <CheckCircle2 size={16} strokeWidth={1.5} />
                  Complete
                </button>
              )}
            </div>

            {/* Download for PDF too */}
            <a
              href={training.material.streamUrl}
              download
              className="flex w-full items-center justify-center gap-2 min-h-[48px] rounded-xl bg-op-muted px-6 py-3 text-sm font-semibold text-op-text-primary active:bg-gray-200 transition-all duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
            >
              <Download size={18} strokeWidth={1.5} />
              Download
            </a>
          </OperatorCard>
        )
      )}

      {/* Description */}
      {training.material.description && (
        <OperatorCard>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-op-text-tertiary">
            About this material
          </p>
          <p className="mt-2 text-sm text-op-text-secondary">{training.material.description}</p>
        </OperatorCard>
      )}

      {/* Why this training — shown when gap context or SOP context is available */}
      {(training.gapDescription || training.sopName) && (
        <OperatorCard>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-op-text-tertiary">
            Why this training
          </p>
          <div className="mt-2">
            <WhyBadge
              generatedBy={training.generatedBy}
              sopName={training.sopName}
              gapDescription={training.gapDescription}
              currentScoreLabel={training.currentScoreLabel}
              targetScoreLabel={training.targetScoreLabel}
              variant="expanded"
            />
          </div>
        </OperatorCard>
      )}

      {/* Feedback card for already-completed training (standalone, full widget) */}
      {isCompleted && assignmentId && (
        <OperatorCard>
          <FeedbackWidget
            targetId={assignmentId}
            feedbackType="training_rating"
            questionText="How was this training?"
          />
        </OperatorCard>
      )}

      {/* Complete confirmation BottomSheet */}
      <BottomSheet
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        title="Mark as Complete?"
      >
        <div className="flex flex-col items-center text-center pb-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
            <CheckCircle2 size={32} strokeWidth={1.5} className="text-emerald-600" />
          </div>
          <p className="mt-3 text-sm text-op-text-secondary">
            You've made great progress! Ready to mark this training as complete?
          </p>
        </div>
        {/* Feedback rating -- independent of completion */}
        <div className="border-t border-gray-100 pt-3">
          <FeedbackWidget
            targetId={assignmentId!}
            feedbackType="training_rating"
            questionText="How was this training?"
            compact
          />
        </div>
        <div className="border-t border-gray-100" />
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={handleMarkComplete}
            disabled={completeTraining.isPending}
            className="flex w-full items-center justify-center gap-2 min-h-[48px] rounded-xl bg-emerald-600 px-6 py-3 text-sm font-semibold text-white active:scale-[0.98] active:bg-emerald-700 transition-all duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {completeTraining.isPending ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Completing...
              </>
            ) : (
              <>
                <CheckCircle2 size={16} strokeWidth={1.5} />
                Complete Training
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setShowCompleteModal(false)}
            className="flex w-full items-center justify-center min-h-[48px] rounded-xl bg-op-muted px-6 py-3 text-sm font-semibold text-op-text-primary active:bg-gray-200 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          >
            Not Yet
          </button>
        </div>
      </BottomSheet>
    </div>
  );
}
